// HTML popup peta. Sengaja import relatif biar popupHtml.check.mjs bisa jalan di node.
import { formatDateTime, formatDataTime } from '../../../util/date.js';
import { DISPLAY_DATETIME } from '../../../util/apiDatetime.js';
import { formatNum } from '../../../util/number.js';
import { convertDecimalToDMS } from '../utils/coordinates.js';

export function renderStatusBadge({ color, label }) {
  return `<span class="mz-popup__badge" style="background:${color};">${label}</span>`;
}

export function renderDetailPager(ship, groups, page, idSuffix = '') {
  const total = groups.length;
  const idx = page - 1;
  const group = groups[idx] || groups[0] || { label: '' };
  const dots = total > 1
    ? `<div class="mz-popup__dots">${groups
      .map((g, i) => `<span class="mz-popup__dot${i === idx ? ' mz-popup__dot--active' : ''}"></span>`)
      .join('')}</div>`
    : '';
  return /* html */`
      <div style="display:flex;align-items:center;gap:8px;">
        <div class="mz-popup__pager-label">
          ${group.label}
          <span class="mz-popup__muted">${idx + 1}/${total}</span>
        </div>
        <button class="btn btn-sm btn-outline-mantraz" style="padding:1px 9px;" id="previous-popup-button-${ship.massId}${idSuffix}" ${total < 2 ? 'disabled' : ''}>&lsaquo;</button>
        <button class="btn btn-sm btn-outline-mantraz" style="padding:1px 9px;" id="next-popup-button-${ship.massId}${idSuffix}" ${total < 2 ? 'disabled' : ''}>&rsaquo;</button>
      </div>
      ${dots}
    `;
}

export function renderGroupBody(group) {
  if (!group) return '';

  // Fuel: card kecil per flowmeter (Flow/Temp/Density) + jam datanya sendiri.
  if (group.cards) {
    const cards = group.cards.map((card) => {
      const metrics = card.rows.map((r) => `
          <div style="text-align:center;">
            <div class="mz-popup__label">${r.label}</div>
            <div class="mz-popup__value">${r.value}</div>
          </div>`).join('');
      const time = card.time
        ? `<div class="mz-popup__card-time"><b>${card.time.date}</b> <b>${card.time.time}</b></div>`
        : '';
      return `
          <div class="mz-popup__card">
            <div class="mz-popup__card-title">${card.title}</div>
            <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:4px;">${metrics}</div>
            ${time}
          </div>`;
    }).join('');
    return `<div style="padding:8px 4px 0;">${cards}</div>`;
  }

  // Sections: tiap section punya timestamp di atas + rows, dipisah garis dashed.
  if (group.sections) {
    const html = group.sections.map((section, idx) => {
      const sep = idx > 0 ? '<div class="mz-popup__sep"></div>' : '';
      let timeHtml = '';
      if (section.time) {
        const t = formatDataTime(section.time) || { date: '', time: '' };
        timeHtml = `<div class="mz-popup__section-time"><i class="ri-time-line"></i><b class="mz-popup__strong">${t.date}</b><b class="mz-popup__strong">${t.time}</b></div>`;
      }
      const rowsHtml = (section.rows || []).map((r) => `
          <div class="${r.full ? 'col-12' : 'col-6'}" style="margin-bottom:8px;">
            <div class="mz-popup__label">${r.label}</div>
            <div class="mz-popup__value" style="word-break:break-word;">${r.value}</div>
          </div>`).join('');
      return `${sep}${timeHtml}<div class="row" style="padding:0 4px;">${rowsHtml}</div>`;
    }).join('');
    return `<div style="padding:8px 4px 0;">${html}</div>`;
  }

  // Grid label/value biasa + jam data per page.
  const rows = (group.rows || []).map((r) => `
      <div class="${r.full ? 'col-12' : 'col-6'}" style="margin-bottom:8px;">
        <div class="mz-popup__label">${r.label}</div>
        <div class="mz-popup__value" style="word-break:break-word;">${r.value}</div>
      </div>`).join('');
  let time = '';
  if (group.time) {
    const t = formatDataTime(group.time) || { date: '', time: '' };
    time = `<div class="mz-popup__group-time"><b>${t.date}</b> <b>${t.time}</b></div>`;
  }
  return `<div class="row" style="padding:8px 4px 0;">${rows}</div>${time}`;
}

export function createSatDotTrailPopupContent(ship, data) {
  const timestamp = data?.timestamp || data?.gpsData?.timestamp;
  const datetime = timestamp ? formatDateTime(timestamp * 1000, DISPLAY_DATETIME) : '-';
  const latitude = data?.latitude;
  const longitude = data?.longitude;
  const speed = data?.speed;
  const headingValue = data?.track ?? data?.heading ?? null;
  const headingDisplay = Number.isFinite(Number(headingValue)) ? `${formatNum(Number(headingValue), 2)}°` : '-';
  const portRpm = data?.portRpm;
  const starboardRpm = data?.starboardRpm;
  const centerRpm = data?.centerRpm;
  const currentAe = data?.currentAe;

  const latDMS = latitude ? convertDecimalToDMS(latitude, true) : '-';
  const lngDMS = longitude ? convertDecimalToDMS(longitude, false) : '-';
  const coordinateDisplay = `${latDMS}, ${lngDMS}`;

  // Build RPM HTML dynamically
  let rpmHtml = '';
  if (portRpm !== null && portRpm !== undefined) {
    rpmHtml += `<div class="col-6" style="height: 50px"><p class="mz-popup__strong" style="margin-top: 0; margin-bottom: -10px;">Port RPM</p><p class="mz-popup__strong" style="font-weight: 1000;">${formatNum(portRpm, 2)}</p></div>`;
  }
  if (starboardRpm !== null && starboardRpm !== undefined) {
    rpmHtml += `<div class="col-6" style="height: 50px"><p class="mz-popup__strong" style="margin-top: 0; margin-bottom: -10px;">Starboard RPM</p><p class="mz-popup__strong" style="font-weight: 1000;">${formatNum(starboardRpm, 2)}</p></div>`;
  }
  if (centerRpm !== null && centerRpm !== undefined) {
    rpmHtml += `<div class="col-6" style="height: 50px"><p class="mz-popup__strong" style="margin-top: 0; margin-bottom: -10px;">Center RPM</p><p class="mz-popup__strong" style="font-weight: 1000;">${formatNum(centerRpm, 2)}</p></div>`;
  }
  if (!rpmHtml) {
    rpmHtml = '<div class="col-12" style="height: 50px"><p style="margin-top: 0; margin-bottom: -10px">No RPM data available</p></div>';
  }

  // Determine status class dynamically
  const statusClass = ship.shipStatus || 'online';

  return `
      <div class="row pt-3">
        <div class="col-6 d-flex align-items-center"><h6>${ship.massName || '-'}</h6></div>
        <div class="col-6 d-flex justify-content-end align-items-center pt-0"><span class="status ${statusClass}">${statusClass}</span></div>
      </div>
      <hr style="margin: 12px 0;" />
      <div class="row"><div class="col-12"><p style="margin: 0; font-size: 13px"><b>Time: </b> ${datetime}</p></div></div>
      <hr style="margin: 12px 0;" />
      <div class="row info-section pb-2">
        <div class="col-12" style="height: 60px">
          <p class="mz-popup__strong" style="margin-top: 0; margin-bottom: -10px;">Coordinates</p>
          <p class="mz-popup__strong" style="font-weight: 1000;">${coordinateDisplay}</p>
        </div>
        ${rpmHtml}
        <div class="col-6" style="height: 50px">
          <p class="mz-popup__strong" style="margin-top: 0; margin-bottom: -10px;">Speed</p>
          <p class="mz-popup__strong" style="font-weight: 1000;">${speed !== null ? `${formatNum(speed, 2)} knot` : '-'}</p>
        </div>
        <div class="col-6" style="height: 50px">
          <p class="mz-popup__strong" style="margin-top: 0; margin-bottom: -10px;">Heading</p>
          <p class="mz-popup__strong" style="font-weight: 1000;">${headingDisplay}</p>
        </div>
        <div class="col-6" style="height: 50px">
          <p style="margin-top: 0; margin-bottom: -10px">Active AE</p>
          <p class="mz-popup__strong" style="font-weight: 1000;">${currentAe !== null ? `AE ${currentAe}` : '-'}</p>
        </div>
      </div>
    `;
}
